// Slot-grid logic shared by the Book Appointment page and the lead's
// "Book Appointment" dialog, so the two can't drift apart again.
import { dayHours, branchDayHours, WEEKDAYS } from "@/components/crm/Availability";
import { isHQ } from "@/lib/branchScope";

export const SLOT_MINUTES = 15;
// Sentinel for the doctor Select — Radix cannot hold an empty string value.
export const ANY_DOCTOR = "__any__";
// The branch last booked into, so reception doesn't re-pick it every time.
export const BOOK_BRANCH_KEY = "bookAppointmentBranch";

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Turn a day's working hours into the clickable slot grid.
export function buildSlots(dh, date, bookedSlots) {
  const result = [];
  const brk = dh.break;
  const today = todayISO();
  for (let m = dh.start; m < dh.end; m += SLOT_MINUTES) {
    const isBreak = !!(brk && m >= brk.from && m < brk.to);
    const isBooked = bookedSlots.has(m);
    const isPast = (() => {
      if (date < today) return true;
      if (date > today) return false;
      const now = new Date();
      return m < now.getHours() * 60 + now.getMinutes();
    })();

    const h = Math.floor(m / 60);
    const min = m % 60;
    const hr = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? "AM" : "PM";
    result.push({
      minutes: m,
      label: `${hr}:${String(min).padStart(2, "0")} ${ampm}`,
      timeStr: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
      isBreak,
      isBooked,
      isPast,
      available: !isBreak && !isBooked && !isPast,
    });
  }
  return result;
}

/**
 * The grid for one day.
 *   Specific doctor → their availability, minus their live bookings.
 *   Any doctor (no doctorId) → the BRANCH's hours and closed days; nobody's
 *   bookings are taken out, since none of them apply until check-in.
 * Returns { slots, dayInfo, closedNote }; dayInfo null = no grid that day
 * (dayInfo.fallback = branch hours unreadable, 9–6 used).
 */
export function computeSlots({ doctorId, availability, branch, date, appointments }) {
  if (!doctorId) {
    const dh = branchDayHours(branch, date);
    if (dh.closed) {
      const dayName = WEEKDAYS.find((w) => w.key === dh.day)?.label || "this day";
      return { slots: [], dayInfo: null, closedNote: branch ? `${branch.name} is closed on ${dayName}.` : null };
    }
    return { slots: buildSlots(dh, date, new Set()), dayInfo: dh, closedNote: null };
  }
  if (!availability) return { slots: [], dayInfo: null, closedNote: null };
  const dh = dayHours(availability, date);
  if (!dh) return { slots: [], dayInfo: null, closedNote: null };

  const bookedSlots = new Set();
  (appointments || []).filter((a) =>
    a.doctor_id === doctorId &&
    (a.scheduled_at || "").slice(0, 10) === date &&
    !["cancelled", "no_show"].includes(a.status)
  ).forEach((a) => {
    const s = new Date(a.scheduled_at);
    const startM = s.getHours() * 60 + s.getMinutes();
    const dur = a.duration_minutes || 15;
    for (let m = startM; m < startM + dur; m += SLOT_MINUTES) bookedSlots.add(m);
  });
  return { slots: buildSlots(dh, date, bookedSlots), dayInfo: dh, closedNote: null };
}

/** Branches a user can book into: HQ any; branch-scoped users their own
 * (everyone, if they have none) — BookingDialog's long-standing rule. */
export function bookableBranches(user, branches) {
  const list = branches || [];
  const mine = user?.branch_ids || [];
  return isHQ(user) || !mine.length ? list : list.filter((b) => mine.includes(b.id));
}

/** The first of `preferIds` that is bookable, else the user's only branch,
 * else the first option. */
export function defaultBranch(options, user, ...preferIds) {
  for (const id of preferIds) {
    const b = id && options.find((o) => o.id === id);
    if (b) return b;
  }
  const mine = user?.branch_ids || [];
  return (mine.length === 1 && options.find((b) => b.id === mine[0])) || options[0] || null;
}
